import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { DCSQueryClient } from "../../../../queryClient";
import { SectionHeader } from "../../../../components/SectionHeader/SectionHeader";
import { useToast } from "../../../../Providers/ToastProvider";
import { requests } from "../../../../services/http-common";
import { ContractForm } from "../ContractForm/ContractForm";
import { ContractStatusEnum, TContractForm } from "../Contracts.model";

export const ContractEdit = () => {
  const { leadId, contractId } = useParams<{
    leadId: string;
    contractId: string;
  }>();

  const navigate = useNavigate();
  const { show } = useToast();

  const { data: contract } = useQuery({
    queryKey: [`contract-form-${contractId}`],
    queryFn: () => {
      return requests.get(`/contract/${contractId}`);
    },
    enabled: !!contractId,
  });
  const isDraft = contract?.status === ContractStatusEnum.Draft;

  const submitContract = useMutation({
    mutationFn: (newContract: TContractForm) => {
      if (isDraft) {
        return requests.post(`/contract/draft/${contractId}/submit`, newContract);
      }

      return requests.put(`/contract/${contractId}`, newContract);
    },
    onSuccess: () => {
      show({ detail: isDraft ? "Contract submitted successfully !" : "Contract was updated successfully !" });
      navigate("/sales/contract");
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const saveDraft = useMutation({
    mutationFn: (draft: Partial<TContractForm>) => {
      if (isDraft) {
        return requests.put(`/contract/draft/${contractId}`, draft);
      }

      return requests.put(`/contract/${contractId}`, draft);
    },
    onSuccess: () => {
      show({ detail: isDraft ? "Contract draft saved successfully!" : "Contract saved successfully!" });
      navigate("/sales/contract");
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  if (!leadId) return null;

  return (
    <>
      <SectionHeader title="Edit Contract" />
      <ContractForm
        onSubmit={(values) => submitContract.mutate(values)}
        onSaveDraft={saveDraft.mutate}
        isPending={submitContract.isLoading}
        isDraftPending={saveDraft.isLoading}
        leadId={leadId}
        contractId={contractId}
      />
    </>
  );
};
